// src/pages/master/MenuPage.js
import React, { useEffect, useMemo, useState } from "react";

const MenuPage = () => {
    const token = localStorage.getItem("token");
    const baseUrl = "https://mis.empowerplus.in/api/menus";

    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: "", text: "" });

    // flat + tree
    const [menus, setMenus] = useState([]);
    const [tree, setTree] = useState([]);

    // create form
    const [name, setName] = useState("");
    const [icon, setIcon] = useState("");
    const [path, setPath] = useState("");
    const [parentId, setParentId] = useState(""); // "" => parent menu
    const [position, setPosition] = useState(0);
    const [isActive, setIsActive] = useState(true);

    // view by id
    const [viewId, setViewId] = useState("");
    const [viewData, setViewData] = useState(null);

    // edit modal
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editIcon, setEditIcon] = useState("");
    const [editPath, setEditPath] = useState("");
    const [editParentId, setEditParentId] = useState(""); // optional
    const [editPosition, setEditPosition] = useState(0);
    const [editIsActive, setEditIsActive] = useState(true);

    // ui
    const [search, setSearch] = useState("");
    const [showTree, setShowTree] = useState(true);

    const headersAuth = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    const setFlash = (type, text) => {
        setMsg({ type, text });
    };

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : null;
        } catch {
            return text;
        }
    };

    // ✅ 3) GET ALL MENUS (flat)
    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await fetch(baseUrl, { headers: headersAuth });
            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Failed (${res.status})`);
            }
            const data = await safeJson(res);
            setMenus(Array.isArray(data) ? data : []);
        } catch (e) {
            setFlash("err", e.message || "Failed to load menus");
        } finally {
            setLoading(false);
        }
    };

    // ✅ 4) GET MENUS AS TREE
    const fetchTree = async () => {
        try {
            const res = await fetch(`${baseUrl}/tree`, { headers: headersAuth });
            if (!res.ok) return; // tree endpoint optional
            const data = await safeJson(res);
            setTree(Array.isArray(data) ? data : []);
        } catch {
            // ignore tree fetch errors
        }
    };

    const refresh = async () => {
        await fetchAll();
        await fetchTree();
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ 1 & 2) CREATE MENU (parent/child)
    const createMenu = async (e) => {
        e.preventDefault();

        if (!name.trim()) return setFlash("err", "Menu name is required");
        const isChild = String(parentId).trim() !== "";
        if (isChild && !path.trim()) return setFlash("err", "Path is required for child menus");

        try {
            const payload = {
                name: name.trim(),
                icon: icon.trim(),
                path: path.trim(),
                isActive: !!isActive,
                position: Number(position) || 0,
            };

            // if child menu
            if (String(parentId).trim() !== "") payload.parentId = Number(parentId);

            const res = await fetch(baseUrl, {
                method: "POST",
                headers: headersAuth,
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Create failed (${res.status})`);
            }

            setFlash("ok", payload.parentId ? "Child menu created" : "Parent menu created");
            setName("");
            setIcon("");
            setPath("");
            setParentId("");
            setPosition(0);
            setIsActive(true);
            refresh();
        } catch (e2) {
            setFlash("err", e2.message || "Create failed");
        }
    };

    // ✅ 5) GET MENU BY ID
    const getById = async () => {
        if (!viewId) return setFlash("err", "Enter an ID to fetch");
        setViewData(null);

        try {
            const res = await fetch(`${baseUrl}/${viewId}`, { headers: headersAuth });
            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Not found (${res.status})`);
            }
            const data = await safeJson(res);
            setViewData(data);
            setFlash("ok", `Fetched menu #${viewId}`);
        } catch (e) {
            setFlash("err", e.message || "Fetch by ID failed");
        }
    };

    // ✅ 6/7) UPDATE MENU (full or soft)
    const openEdit = (row) => {
        setEditingId(row.id);
        setEditName(row.name || "");
        setEditIcon(row.icon || "");
        setEditPath(row.path || "");
        setEditParentId(row.parentId === null || row.parentId === undefined ? "" : String(row.parentId));
        setEditPosition(row.position ?? 0);
        setEditIsActive(!!row.isActive);
    };

    const closeEdit = () => {
        setEditingId(null);
        setEditName("");
        setEditIcon("");
        setEditPath("");
        setEditParentId("");
        setEditPosition(0);
        setEditIsActive(true);
    };

    const updateMenu = async () => {
        if (!editingId) return;

        try {
            const payload = {};
            if (editName.trim() !== "") payload.name = editName.trim();
            if (editIcon.trim() !== "") payload.icon = editIcon.trim();
            if (editPath.trim() !== "") payload.path = editPath.trim();
            payload.isActive = !!editIsActive;
            payload.position = Number(editPosition) || 0;

            if (editParentId.trim() === "") payload.parentId = null;
            else payload.parentId = Number(editParentId);

            const res = await fetch(`${baseUrl}/${editingId}`, {
                method: "PUT",
                headers: headersAuth,
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Update failed (${res.status})`);
            }

            setFlash("ok", "Menu updated");
            closeEdit();
            refresh();
        } catch (e) {
            setFlash("err", e.message || "Update failed");
        }
    };

    // ✅ 8) DELETE MENU
    const deleteMenu = async (id) => {
        const ok = window.confirm(`Delete menu #${id}?`);
        if (!ok) return;

        try {
            const res = await fetch(`${baseUrl}/${id}`, {
                method: "DELETE",
                headers: headersAuth,
            });

            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Delete failed (${res.status})`);
            }

            setFlash("ok", "Menu deleted");
            if (String(viewId) === String(id)) setViewData(null);
            refresh();
        } catch (e) {
            setFlash("err", e.message || "Delete failed");
        }
    };

    const parents = useMemo(() => menus.filter((m) => m.parentId == null), [menus]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return menus;
        return menus.filter((m) => {
            const n = (m.name || "").toLowerCase();
            const p = (m.path || "").toLowerCase();
            return n.includes(q) || p.includes(q) || String(m.id).includes(q);
        });
    }, [menus, search]);

    const TreeNode = ({ node, level = 0 }) => {
        const hasChildren = node.children && node.children.length > 0;
        return (
            <div style={{ ...styles.treeRow, paddingLeft: 12 + level * 16 }}>
                <div style={styles.treeLeft}>
                    <span style={styles.treeBullet}>{hasChildren ? "▸" : "•"}</span>
                    <span style={styles.treeName}>{node.name}</span>
                    <span style={styles.treeMeta}>
                        #{node.id} · pos:{node.position ?? 0} {node.path ? `· ${node.path}` : ""}{" "}
                        {node.isActive === false ? "· Inactive" : ""}
                    </span>
                </div>
                {hasChildren ? (
                    <div style={styles.treeChildren}>
                        {node.children.map((c) => (
                            <TreeNode key={c.id} node={c} level={level + 1} />
                        ))}
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div style={styles.page}>
            <div style={styles.headerRow}>
                <div>
                    <div style={styles.title}>Menu Master</div>
                    <div style={styles.subTitle}>Parent/Child menus + Tree for Sidebar</div>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                    <button style={styles.secondaryBtn} onClick={() => setShowTree((s) => !s)}>
                        {showTree ? "Hide Tree" : "Show Tree"}
                    </button>
                    <button style={styles.refreshBtn} onClick={refresh} disabled={loading}>
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>
                </div>
            </div>

            {msg.text ? (
                <div
                    style={{
                        ...styles.flash,
                        ...(msg.type === "ok" ? styles.flashOk : styles.flashErr),
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                >
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            ) : null}

            {/* CREATE */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>Create Menu (Parent or Child)</div>

                <form onSubmit={createMenu} style={styles.formGrid}>
                    <div style={styles.field}>
                        <label style={styles.label}>Name *</label>
                        <input
                            style={styles.input}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Masters"
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Icon</label>
                        <input
                            style={styles.input}
                            value={icon}
                            onChange={(e) => setIcon(e.target.value)}
                            placeholder='e.g. "ti ti-settings"'
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>
                            Path {String(parentId).trim() !== "" ? "*" : "(optional)"}
                        </label>
                        <input
                            style={styles.input}
                            value={path}
                            onChange={(e) => setPath(e.target.value)}
                            placeholder={String(parentId).trim() !== "" ? "/masters/departments" : "leave empty for parent menu"}
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Parent Menu (optional)</label>
                        <select
                            style={styles.input}
                            value={parentId}
                            onChange={(e) => setParentId(e.target.value)}
                        >
                            <option value="">None (Create Parent)</option>
                            {parents.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name} (#{p.id})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Position (order)</label>
                        <input
                            style={styles.input}
                            type="number"
                            min="0"
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            placeholder="0 = top, 1, 2, 3..."
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Status</label>
                        <div style={styles.toggleRow}>
                            <button
                                type="button"
                                onClick={() => setIsActive(true)}
                                style={{
                                    ...styles.pill,
                                    ...(isActive ? styles.pillOn : styles.pillOff),
                                }}
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsActive(false)}
                                style={{
                                    ...styles.pill,
                                    ...(!isActive ? styles.pillOn : styles.pillOff),
                                }}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>

                    <div style={{ ...styles.field, alignItems: "flex-end" }}>
                        <button type="submit" style={styles.primaryBtn}>
                            Create
                        </button>
                    </div>
                </form>

                <div style={styles.note}>
                    Tip: To create a child menu, select a Parent Menu. Leave it empty to create a parent.
                </div>
            </div>

            {/* GET BY ID */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>Get Menu By ID</div>

                <div style={styles.row}>
                    <input
                        style={{ ...styles.input, maxWidth: 220 }}
                        value={viewId}
                        onChange={(e) => setViewId(e.target.value)}
                        placeholder="Enter ID (e.g. 2)"
                    />
                    <button style={styles.primaryBtn} onClick={getById}>
                        Fetch
                    </button>
                    <button
                        style={styles.secondaryBtn}
                        onClick={() => {
                            setViewData(null);
                            setViewId("");
                        }}
                    >
                        Clear
                    </button>
                </div>

                {viewData ? (
                    <div style={styles.viewBox}>
                        <div style={styles.kv}>
                            <span style={styles.k}>ID</span>
                            <span style={styles.v}>{viewData.id}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Name</span>
                            <span style={styles.v}>{viewData.name}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Icon</span>
                            <span style={styles.v}>{viewData.icon || "-"}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Path</span>
                            <span style={styles.v}>{viewData.path || "-"}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Parent ID</span>
                            <span style={styles.v}>
                                {viewData.parentId == null ? "null" : viewData.parentId}
                            </span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Position</span>
                            <span style={styles.v}>{viewData.position ?? 0}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Active</span>
                            <span style={styles.v}>{viewData.isActive ? "true" : "false"}</span>
                        </div>
                    </div>
                ) : (
                    <div style={styles.muted}>No record loaded</div>
                )}
            </div>

            {/* TREE */}
            {showTree ? (
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Menu Tree (Sidebar)</div>
                    {tree.length === 0 ? (
                        <div style={styles.muted}>No tree data (check GET /api/menus/tree)</div>
                    ) : (
                        <div style={styles.treeWrap}>
                            {tree.map((n) => (
                                <TreeNode key={n.id} node={n} />
                            ))}
                        </div>
                    )}
                </div>
            ) : null}

            {/* LIST */}
            <div style={styles.card}>
                <div style={styles.cardTop}>
                    <div style={styles.cardTitle}>All Menus (Flat)</div>
                    <input
                        style={{ ...styles.input, maxWidth: 320 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name / path / id..."
                    />
                </div>

                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>ID</th>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Icon</th>
                                <th style={styles.th}>Path</th>
                                <th style={styles.th}>ParentId</th>
                                <th style={styles.th}>Pos</th>
                                <th style={styles.th}>Active</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td style={styles.td} colSpan={8}>
                                        {loading ? "Loading..." : "No menus found"}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((m) => (
                                    <tr key={m.id}>
                                        <td style={styles.td}>{m.id}</td>
                                        <td style={styles.td}>{m.name}</td>
                                        <td style={styles.td}>{m.icon || "-"}</td>
                                        <td style={styles.td}>{m.path || "-"}</td>
                                        <td style={styles.td}>{m.parentId == null ? "null" : m.parentId}</td>
                                        <td style={styles.td}>{m.position ?? 0}</td>
                                        <td style={styles.td}>
                                            <span
                                                style={{
                                                    ...styles.badge,
                                                    ...(m.isActive ? styles.badgeOn : styles.badgeOff),
                                                }}
                                            >
                                                {m.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <button style={styles.smallBtn} onClick={() => openEdit(m)}>
                                                Edit
                                            </button>
                                            <button
                                                style={{ ...styles.smallBtn, ...styles.dangerBtn }}
                                                onClick={() => deleteMenu(m.id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* EDIT MODAL */}
            {editingId ? (
                <div style={styles.modalOverlay} onMouseDown={closeEdit}>
                    <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
                        <div style={styles.modalTitle}>Update Menu</div>

                        <div style={styles.formGrid2}>
                            <div style={styles.field}>
                                <label style={styles.label}>Name</label>
                                <input
                                    style={styles.input}
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    placeholder="Departments"
                                />
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label}>Icon</label>
                                <input
                                    style={styles.input}
                                    value={editIcon}
                                    onChange={(e) => setEditIcon(e.target.value)}
                                    placeholder="business"
                                />
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label}>Path</label>
                                <input
                                    style={styles.input}
                                    value={editPath}
                                    onChange={(e) => setEditPath(e.target.value)}
                                    placeholder="/masters/departments"
                                />
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label}>Parent Menu</label>
                                <select
                                    style={styles.input}
                                    value={editParentId}
                                    onChange={(e) => setEditParentId(e.target.value)}
                                >
                                    <option value="">None (parent)</option>
                                    {parents
                                        .filter((p) => p.id !== editingId)
                                        .map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} (#{p.id})
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label}>Position (order)</label>
                                <input
                                    style={styles.input}
                                    type="number"
                                    min="0"
                                    value={editPosition}
                                    onChange={(e) => setEditPosition(e.target.value)}
                                    placeholder="0 = top, 1, 2, 3..."
                                />
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label}>Active</label>
                                <div style={styles.toggleRow}>
                                    <button
                                        type="button"
                                        onClick={() => setEditIsActive(true)}
                                        style={{
                                            ...styles.pill,
                                            ...(editIsActive ? styles.pillOn : styles.pillOff),
                                        }}
                                    >
                                        Active
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditIsActive(false)}
                                        style={{
                                            ...styles.pill,
                                            ...(!editIsActive ? styles.pillOn : styles.pillOff),
                                        }}
                                    >
                                        Inactive
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={styles.modalActions}>
                            <button style={styles.secondaryBtn} onClick={closeEdit}>
                                Cancel
                            </button>
                            <button style={styles.primaryBtn} onClick={updateMenu}>
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const styles = {
    page: {
        padding: 24,
        background: "#f7f7fb",
        minHeight: "100vh",
        fontFamily: "Inter, system-ui, Arial",
        color: "#111827",
    },
    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 14,
    },
    title: { fontSize: 28, fontWeight: 800, letterSpacing: 0.2 },
    subTitle: { marginTop: 4, color: "#6b7280", fontSize: 13 },
    refreshBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 800,
    },

    flash: {
        marginBottom: 14,
        padding: "10px 12px",
        borderRadius: 10,
        fontSize: 14,
    },
    flashOk: { background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" },
    flashErr: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" },

    card: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 16,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    },
    cardTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
    },
    cardTitle: { fontSize: 16, fontWeight: 900, marginBottom: 10 },

    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(6, minmax(150px, 1fr))",
        gap: 12,
    },
    formGrid2: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(180px, 1fr))",
        gap: 12,
    },

    field: { display: "flex", flexDirection: "column", gap: 6 },
    label: { fontSize: 12, color: "#6b7280", fontWeight: 800 },

    input: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        outline: "none",
        fontSize: 14,
        background: "#fff",
    },

    note: { marginTop: 10, color: "#6b7280", fontSize: 12 },

    row: { display: "flex", gap: 10, alignItems: "center" },

    toggleRow: { display: "flex", gap: 8 },
    pill: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "8px 12px",
        borderRadius: 999,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 800,
    },
    pillOn: { background: "#111827", color: "#fff", border: "1px solid #111827" },
    pillOff: { opacity: 0.6 },

    primaryBtn: {
        border: "1px solid #2563eb",
        background: "#2563eb",
        color: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
    },
    secondaryBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        color: "#111827",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
    },

    muted: { marginTop: 10, color: "#6b7280", fontSize: 13 },

    viewBox: {
        marginTop: 10,
        border: "1px dashed #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#fafafa",
    },
    kv: {
        display: "grid",
        gridTemplateColumns: "140px 1fr",
        gap: 10,
        padding: "6px 0",
        borderBottom: "1px solid #eee",
    },
    k: { color: "#6b7280", fontWeight: 900, fontSize: 12 },
    v: { fontWeight: 700, fontSize: 13 },

    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
        textAlign: "left",
        fontSize: 12,
        color: "#6b7280",
        fontWeight: 900,
        padding: "10px 10px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fcfcff",
    },
    td: {
        padding: "10px 10px",
        borderBottom: "1px solid #f1f5f9",
        fontSize: 14,
    },

    badge: {
        display: "inline-flex",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        border: "1px solid transparent",
    },
    badgeOn: { background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" },
    badgeOff: { background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" },

    smallBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "7px 10px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
        marginRight: 8,
    },
    dangerBtn: { borderColor: "#fecaca", color: "#991b1b", background: "#fff5f5" },

    treeWrap: { border: "1px solid #eef2ff", borderRadius: 12, overflow: "hidden" },
    treeRow: { borderBottom: "1px solid #eef2ff" },
    treeLeft: { display: "flex", gap: 8, alignItems: "center", padding: "10px 12px" },
    treeBullet: { width: 16, textAlign: "center", color: "#6b7280" },
    treeName: { fontWeight: 900 },
    treeMeta: { marginLeft: 8, color: "#6b7280", fontSize: 12, fontWeight: 700 },
    treeChildren: {},

    modalOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
    },
    modal: {
        width: "min(760px, 100%)",
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        padding: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
    },
    modalTitle: { fontSize: 16, fontWeight: 900, marginBottom: 10 },
    modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 },
};

export default MenuPage;
